import { Card, type CardProps } from 'gooey';
import React, { type ElementType } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

import { Glow } from './glow';

export type GlowBorder =
  | 'all'
  | 'none'
  | 'top'
  | 'right'
  | 'bottom'
  | 'left'
  | 'top-right'
  | 'top-bottom'
  | 'top-left'
  | 'right-bottom'
  | 'right-left'
  | 'bottom-left'
  | 'top-right-bottom'
  | 'top-right-left'
  | 'top-bottom-left'
  | 'right-bottom-left';

export type GlowLocation = 'top' | 'bottom' | 'left' | 'right';

export interface GlowCardProps extends Omit<CardProps, 'variant' | 'background'> {
  /**
   * The borders to apply. Set to false to remove a border.
   * @default all
   */
  border?: GlowBorder;
  /**
   * The location of the glow.
   */
  location?: GlowLocation;
  /**
   * height of the glow
   * @default 50
   */
  glowHeight?: string | number;
  /**
   * The amount of space to apply (padding)
   */
  gutter?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'none';
  /**
   * The content of the component.
   */
  children?: React.ReactNode;
}

function GlowCard<C extends ElementType = 'div'>({
  border = 'all',
  location = 'top',
  gutter = 'md',
  children,
  glowHeight = 50,
  className,
  ...props
}: GlowCardProps) {
  const classNames = React.useMemo(
    () => ({
      border: getBorderClasses(border),
      glow: getGlowClasses(location),
      gutter: getGutterClasses(gutter),
    }),
    [border, location, gutter]
  );

  return (
    <Card
      {...props}
      className={twMerge(
        'relative overflow-hidden z-0 border-divider',
        classNames.border,
        classNames.gutter,
        className
      )}
      variant='border'
      background='black'
    >
      <Glow className={twJoin(classNames.glow, '-z-10')} />
      {children}
    </Card>
  );
}
GlowCard.displayName = 'GlowCard';
export { GlowCard };

/*
|------------------
| Internals
|------------------
*/

function getGutterClasses(gutter?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'none') {
  switch (gutter) {
    case 'xs':
      return 'p-8';
    case 'sm':
      return 'p-8 sm:p-10';
    case 'md':
      return 'p-8 sm:p-10 md:p-12';
    case 'lg':
      return 'p-8 sm:p-10 md:p-12 lg:p-14';
    case 'xl':
      return 'p-8 sm:p-10 md:p-12 lg:p-14 xl:p-16';
    case 'none':
      return '';
    default:
      return 'p-6';
  }
}

function getGlowClasses(location: GlowLocation): string {
  switch (location) {
    case 'top':
      return 'top-0 right-0 left-0';
    case 'right':
      return 'right-0 top-0 bottom-0 h-full w-12';
    case 'left':
      return 'left-0 top-0 bottom-0 h-full w-12';
    case 'bottom':
      return 'bottom-0 right-0 left-0';
    default:
      return 'top-0 right-0 left-0';
  }
}

export const glowBorderClassNames = {
  // All or none
  all: 'border',
  none: 'border-0',

  // Single borders
  top: 'border-t border-r-0 border-b-0 border-l-0',
  right: 'border-t-0 border-r border-b-0 border-l-0',
  bottom: 'border-t-0 border-r-0 border-b border-l-0',
  left: 'border-t-0 border-r-0 border-b-0 border-l',

  // Two borders
  'top-right': 'border-t border-r border-b-0 border-l-0',
  'top-bottom': 'border-t border-r-0 border-b border-l-0',
  'top-left': 'border-t border-r-0 border-b-0 border-l',
  'right-bottom': 'border-t-0 border-r border-b border-l-0',
  'right-left': 'border-t-0 border-r border-b-0 border-l',
  'bottom-left': 'border-t-0 border-r-0 border-b border-l',

  // Three borders
  'top-right-bottom': 'border-t border-r border-b border-l-0',
  'top-right-left': 'border-t border-r border-b-0 border-l',
  'top-bottom-left': 'border-t border-r-0 border-b border-l',
  'right-bottom-left': 'border-t-0 border-r border-b border-l',
} as const;

function getBorderClasses(border: GlowBorder = 'all') {
  return glowBorderClassNames[border];
}
